import { createReducer } from '@reduxjs/toolkit';

import { IssueDetails } from '../../services/api/dto/Issues';
import { OperationsSignStateType } from '../../types';
import { getExchange } from '../actions/exchange';
import {
  getAgreementDoc,
  getCreditAgreement,
  getCreditConstraints,
  getDecisionDoc,
  getIssue,
  getLastPurchases,
  getLastSale,
  getPurchase,
  getPurchasesOperator,
  getPurchasesOperatorCompany,
  getSale,
  getSaleAgreement,
  getUcpDoc,
  initCredit,
  initExchange,
  initPurchase,
  initSale,
  initUcp,
  rejectPurchase,
  releaseCfa,
  setCreditDetails,
  setOperationsSignLoading,
  setPaymentIds,
  setSignedDoc,
  setSigningInfo,
  setSignResultDoc,
} from '../actions/operations-sign';
import { initIssuePayment } from '../actions/order-realization-detailed';

const initialState: OperationsSignStateType = {
  data: {
    releaseCfa: {},
    buyingCfa: {},
    credit: {},
    saleCfa: {},
    orderUcp: {},
    lastPurchase: {},
    lastSale: {},
    purchaseOperator: {},
    purchaseOperatorCompany: {},
    purchase: {},
    sale: {},
    exchange: {},
  },
  loading: {
    credit: false,
    creditDoc: false,
    creditSign: false,
    releaseCfa: false,
    releaseCfaDoc: false,
    releaseCfaSign: false,
    buyingCfa: false,
    buyingCfaDoc: false,
    creditConstraints: false,
    buyingCfaSign: false,
    saleCfa: false,
    saleCfaDoc: false,
    saleCfaSign: false,
    signUcp: false,
    initUcp: false,
    signUcpDoc: false,
    lastPurchase: false,
    lastSale: false,
    purchaseOperator: false,
    purchaseOperatorCompany: false,
    rejectPurchase: false,
    getPurchase: false,
    getSale: false,
    getIssue: false,
    exchange: false,
  },
};

const operationsSignReducer = createReducer(initialState, (builder) => {
  builder.addCase(releaseCfa.fulfilled, (state, action) => {
    if (action.payload) {
      state.data.releaseCfa.decisionId = action.payload.id;
      state.data.releaseCfa.issuingInfoIds = action.payload.issuingInfoIds;
      state.data.releaseCfa.documentType = action.payload.documentType;
      state.data.releaseCfa.claim = action.payload.claim;
      state.data.newReleaseCfa = action.payload.data;
    }
  });

  builder.addCase(getDecisionDoc.fulfilled, (state, action) => {
    if (action.payload) {
      state.data.releaseCfa.decisionDoc = action.payload.doc;
      state.data.releaseCfa.decisionHash = action.payload.hash;
    }
  });

  builder.addCase(setSigningInfo, (state, action) => {
    state.data.signingInfo = action.payload;
  });

  builder.addCase(setSignedDoc, (state, action) => {
    state.data[action.payload.name].signedDoc = action.payload.value;
  });

  builder.addCase(initPurchase.fulfilled, (state, action) => {
    if (action.payload) {
      state.data.buyingCfa = {
        ...action.payload,
        decisionId: action.payload.id,
        claim: action.payload.amount,
      };
    }
  });

  builder.addCase(initSale.fulfilled, (state, action) => {
    if (action.payload) {
      state.data.saleCfa = {
        ...action.payload,
        decisionId: action.payload.id,
        claim: action.payload.amount,
      };
    }
  });

  builder.addCase(getAgreementDoc.fulfilled, (state, action) => {
    console.log("builder.addCase(getAgreementDoc.fulfilled, (state, action) => {", action.payload);

    if (action.payload) {
      state.data.buyingCfa.decisionDoc = action.payload.doc;
      state.data.buyingCfa.decisionHash = action.payload.hash;
    }
  });

  builder.addCase(getSaleAgreement.fulfilled, (state, action) => {
    if (action.payload) {
      state.data.saleCfa.decisionDoc = action.payload.doc;
      state.data.saleCfa.decisionHash = action.payload.hash;
    }
  });

  builder.addCase(getCreditConstraints.fulfilled, (state, action) => {
    if (action.payload) {
      state.data.credit.constraints = action.payload;
    }
  });

  builder.addCase(initCredit.fulfilled, (state, action) => {
    if (action.payload) {
      state.data.credit.decisionId = action.payload.id;
    }
  });

  builder.addCase(getCreditAgreement.fulfilled, (state, action) => {
    if (action.payload) {
      state.data.credit.decisionDoc = action.payload.doc;
      state.data.credit.decisionHash = action.payload.hash;
    }
  });

  builder.addCase(setCreditDetails, (state, action) => {
    state.data.credit.details = action.payload;
  });

  builder.addCase(setOperationsSignLoading, (state, action) => {
    state.loading[action.payload.name] = action.payload.value;
  });

  builder.addCase(initUcp.fulfilled, (state, action) => {
    if (action.payload) {
      state.data.orderUcp.decisionId = action.payload.id;
      state.data.orderUcp.documentType = action.payload.documentType;
    }
  });

  builder.addCase(initIssuePayment.fulfilled, (state, action) => {
    if (action.payload) {
      state.data.orderUcp.decisionId = action.payload.id;
    }
  });

  builder.addCase(getUcpDoc.fulfilled, (state, action) => {
    if (action.payload) {
      state.data.orderUcp.decisionDoc = action.payload.doc;
      state.data.orderUcp.decisionHash = action.payload.hash;
    }
  });

  builder.addCase(setSignResultDoc, (state, action) => {
    state.data[action.payload.name].signedDoc = action.payload.value;
  });

  builder.addCase(setPaymentIds, (state, action) => {
    state.data.orderUcp.paymentItemsIds = action.payload;
  });

  builder.addCase(getLastSale.fulfilled, (state, action) => {
    state.data.lastSale = action.payload;
  });

  builder.addCase(getLastPurchases.fulfilled, (state, action) => {
    state.data.lastPurchase = action.payload;
  });

  builder.addCase(getPurchasesOperator.fulfilled, (state, action) => {
    state.data.purchaseOperator = action.payload;
  });

  builder.addCase(getPurchasesOperatorCompany.fulfilled, (state, action) => {
    state.data.purchaseOperatorCompany = action.payload;
  });

  builder.addCase(rejectPurchase.fulfilled, (state, action) => {
    state.data.lastPurchase = {};
  });

  builder.addCase(getPurchase.fulfilled, (state, action) => {
    state.data.purchase = action.payload;
  });

  builder.addCase(getSale.fulfilled, (state, action) => {
    state.data.sale = action.payload;
  });

  builder.addCase(getIssue.fulfilled, (state, action) => {
    state.data.issue = action.payload as IssueDetails;
  });

  builder.addCase(initExchange.fulfilled, (state, action) => {
    state.data.exchange = action.payload;
  });

  builder.addCase(getExchange.fulfilled, (state, action) => {
    state.data.exchange = action.payload;
  });
});

export default operationsSignReducer;
